import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import PropTypes from "prop-types";
import { useController } from "react-hook-form";
import { createUseStyles } from "react-jss";
import classNames from "classnames";
import { useState } from "react";
import inputRelationalNtoNOrderingSkin from "../../../../skins/input-relational-n-to-n-ordering.skin";

const useStyles = createUseStyles(inputRelationalNtoNOrderingSkin);

function SelectMultipleWithOrdering(props) {
  const {
    permittedValues,
    control,
    setValue,
    name: originalName,
    value: originalValue,
  } = props;

  const classes = useStyles();

  const [searchTerm, setSearchTerm] = useState("");

  const {
    field: { value },
  } = useController({
    name: originalName,
    control,
    defaultValue: originalValue,
    shouldUnregister: true,
  });

  let options = [];

  if (permittedValues !== null) {
    Object.keys(permittedValues).forEach((optionKey) => {
      const optionData = permittedValues[optionKey];
      options.push({
        value: optionData.id,
        label: optionData.title,
      });
    });
  }

  const selectedOptions = value
    .map((id) => options.find((option) => option.value === id))
    .filter((option) => option !== undefined);

  const unselectedOptions = options.filter(
    (option) => !value.includes(option.value)
  );

  const filteredUnselectedOptions = unselectedOptions.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedContainerId = `${originalName}-selected`;
  const unselectedContainerId = `${originalName}-unselected`;

  const onDragEnd = (result) => {
    if (!result.destination || isNaN(parseInt(result.draggableId, 10))) {
      return;
    }

    if (result.destination.droppableId === selectedContainerId) {
      const indexValue = result.destination.index;

      let newValue = [...value];

      if (result.source.droppableId === selectedContainerId) {
        newValue = value.filter((id) => id !== result.draggableId);
      }

      if (indexValue === 0) {
        setValue(originalName, [result.draggableId, ...newValue]);
      } else if (indexValue === value.length) {
        setValue(originalName, [...newValue, result.draggableId]);
      } else {
        setValue(originalName, [
          ...newValue.slice(0, indexValue),
          result.draggableId,
          ...newValue.slice(indexValue),
        ]);
      }
    } else if (result.destination.droppableId === unselectedContainerId) {
      setValue(
        originalName,
        value.filter((id) => id !== result.draggableId)
      );
    }
  };

  return (
    <>
      <div className={classes["drag-n-drop-board"]}>
        <div className={classes["selected-items-info-container"]}>
          <div>{value.length} items selected</div>
          <div>
            <input
              type="button"
              onClick={() => setValue(originalName, [])}
              value="Remove all"
              className={classes["select-all-none-button"]}
            />
          </div>
        </div>
        <div className={classes["unselected-items-info-container"]}>
          <div>
            <input
              type="search"
              className="form-control"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </div>
          <div>
            <input
              type="button"
              className={classes["select-all-none-button"]}
              onClick={() =>
                setValue(
                  originalName,
                  options.map((option) => option.value)
                )
              }
              value="Select all"
            />
          </div>
        </div>
      </div>
      <div className={classes["drag-n-drop-board"]}>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId={selectedContainerId} direction="vertical">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className={classes["draggable-container"]}
              >
                {selectedOptions.map((option, index) => (
                  <Draggable
                    key={option.value}
                    draggableId={`${option.value}`}
                    index={index}
                  >
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={classNames(
                          classes["draggable-item"],
                          classes["draggable-item-selected"]
                        )}
                      >
                        <div>
                          <span className="fas fa-arrows-alt-v"></span>{" "}
                          <span>{option.label}</span>
                        </div>
                        <div>
                          <button
                            type="button"
                            className={classes["add-remove-button"]}
                            onClick={() =>
                              setValue(
                                originalName,
                                value.filter((id) => id !== option.value)
                              )
                            }
                          >
                            <span className="fas fa-minus" />
                          </button>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
          <Droppable droppableId={unselectedContainerId} direction="vertical">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className={classes["draggable-container"]}
              >
                {filteredUnselectedOptions.map((option, index) => (
                  <Draggable
                    key={option.value}
                    draggableId={`${option.value}`}
                    index={index}
                  >
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={classes["draggable-item"]}
                      >
                        <div>
                          <span className="fas fa-arrows-alt"></span>{" "}
                          <span>{option.label}</span>
                        </div>
                        <div>
                          <button
                            type="button"
                            className={classes["add-remove-button"]}
                            onClick={() =>
                              setValue(originalName, value.concat(option.value))
                            }
                          >
                            <span className="fas fa-plus" />
                          </button>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
    </>
  );
}

SelectMultipleWithOrdering.propTypes = {
  className: PropTypes.string,
  control: PropTypes.object,
  id: PropTypes.string,
  isNullable: PropTypes.bool,
  name: PropTypes.string,
  permittedValues: PropTypes.array,
  required: PropTypes.bool,
  value: PropTypes.array,
};

export default SelectMultipleWithOrdering;
